import React from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { BAND_CONTENT, DIMENSION_LABEL, DIMENSION_ORDER, dimensionLevel } from './scoring';
import { createAuditHref, decodeScoreResult } from './scoreResultUrl';
type ScoreResultsPageProps = {
  search: string;
};
export function ScoreResultsPage({
  search
}: ScoreResultsPageProps) {
  const result = decodeScoreResult(search);
  if (!result) {
    return <EmptyScoreState data-id="element-471" />;
  }
  const content = BAND_CONTENT[result.band];
  const auditEmailHref = createAuditHref(result);
  return <main className="min-h-screen w-full bg-paper px-6 py-8 text-text md:px-10 md:py-12" data-id="element-472">
      <div className="mx-auto max-w-4xl" data-id="element-473">
        <a href="/" className="inline-flex items-center gap-2 rounded-xl px-2 py-2 text-sm font-semibold text-navy transition-colors hover:bg-paper-dark focus:outline-none focus:ring-2 focus:ring-seaglass" data-id="element-474">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-475" />
          Back to Author Leverage
        </a>

        <header className="border-b border-gray-200 pb-10 pt-10 text-center md:pb-12" data-id="element-476">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seaglass" data-id="element-477">
            Your Reader-to-Revenue Score
          </p>
          <p className="mt-4 text-sm font-medium text-text-muted" data-id="element-478">
            {result.totalScore} of 24 points
          </p>
          <h1 className="mx-auto mt-5 max-w-3xl font-serif text-4xl leading-tight text-navy md:text-6xl" data-id="element-479">
            {result.band}
          </h1>
          <p className="mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-text-muted" data-id="element-480">
            {content.acknowledgment}
          </p>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-text-muted" data-id="element-481">
            {content.meaning}
          </p>
        </header>

        <section className="grid gap-5 py-10 text-left md:grid-cols-2 md:py-12" data-id="element-482">
          <article className="rounded-3xl border border-gray-200 bg-white p-7 md:p-8" data-id="element-483">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-seaglass" data-id="element-484">
              Biggest opportunity
            </p>
            <h2 className="mt-3 font-serif text-3xl text-navy" data-id="element-485">
              {DIMENSION_LABEL[result.biggestOpportunity]}
            </h2>
            <p className="mt-4 leading-relaxed text-text-muted" data-id="element-486">
              {content.opportunity}
            </p>
          </article>

          <article className="rounded-3xl border border-gray-200 bg-white p-7 md:p-8" data-id="element-487">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-seaglass" data-id="element-488">
              Strongest layer
            </p>
            <h2 className="mt-3 font-serif text-3xl text-navy" data-id="element-489">
              {DIMENSION_LABEL[result.strongestLayer]}
            </h2>
            <p className="mt-4 leading-relaxed text-text-muted" data-id="element-490">
              {content.nextStep}
            </p>
          </article>
        </section>

        <section className="border-t border-gray-200 py-10 text-left md:py-12" aria-labelledby="dimensions-heading" data-id="element-491">
          <p className="mb-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-seaglass" data-id="element-492">
            Your four layers
          </p>
          <h2 id="dimensions-heading" className="sr-only" data-id="element-493">
            Your four score dimensions
          </h2>
          <div className="grid gap-3 sm:grid-cols-2" data-id="element-494">
            {DIMENSION_ORDER.map(dimension => <article key={dimension} className="rounded-2xl border border-gray-200 bg-white px-5 py-4" data-id="element-495">
                <div className="flex items-baseline justify-between gap-4" data-id="element-496">
                  <h3 className="font-semibold text-navy" data-id="element-497">
                    {DIMENSION_LABEL[dimension]}
                  </h3>
                  <p className="text-sm font-medium text-text-muted" data-id="element-498">
                    {result.dimensions[dimension]} / 6
                  </p>
                </div>
                <p className="mt-1 text-sm text-text-muted" data-id="element-499">
                  {dimensionLevel(result.dimensions[dimension])}
                </p>
              </article>)}
          </div>
        </section>

        <section className="rounded-3xl bg-navy px-7 py-10 text-center text-paper md:px-12" aria-labelledby="audit-heading" data-id="element-500">
          <h2 id="audit-heading" className="font-serif text-3xl leading-tight md:text-4xl" data-id="element-501">
            See what a clearer path could unlock.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-paper/75" data-id="element-502">
            {content.ctaHelper}
          </p>
          <a href={auditEmailHref} className="mt-7 inline-flex min-h-[56px] items-center justify-center rounded-xl bg-sand px-8 py-4 font-bold text-navy transition-colors hover:bg-sand-dark focus:outline-none focus:ring-2 focus:ring-white" data-id="element-503">
            Explore a Reader Path Audit
            <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" data-id="element-504" />
          </a>
        </section>
      </div>
    </main>;
}
function EmptyScoreState() {
  return <main className="flex min-h-screen w-full items-center justify-center bg-paper px-6 py-12 text-text" data-id="element-505">
      <section className="w-full max-w-xl rounded-3xl border border-gray-200 bg-white p-8 text-center shadow-sm md:p-12" aria-labelledby="empty-score-heading" data-id="element-506">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seaglass" data-id="element-507">
          Reader-to-Revenue Score
        </p>
        <h1 id="empty-score-heading" className="mt-4 font-serif text-4xl leading-tight text-navy" data-id="element-508">
          This score link is incomplete or invalid.
        </h1>
        <p className="mt-5 leading-relaxed text-text-muted" data-id="element-509">
          Complete the eight-question check to generate a fresh score page.
        </p>
        <a href="/" className="mt-8 inline-flex min-h-[48px] items-center justify-center rounded-xl bg-sand px-6 py-3 font-bold text-navy transition-colors hover:bg-sand-dark focus:outline-none focus:ring-2 focus:ring-seaglass" data-id="element-510">
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" data-id="element-511" />
          Return to Author Leverage
        </a>
      </section>
    </main>;
}